/**
 * Lokalisierte Datums-/Zeitformatierung der I18n_Infrastruktur.
 *
 * Bindet die Formatierung von Datums- und Zeitangaben an die Aktive_Sprache
 * (Req 9.1). Statt `undefined` als Locale (wie bisher in den `toLocaleDateString`-
 * Aufrufen von `ProfilePage`/`WeeklyPlanView`) wird die zur Sprache passende
 * BCP-47-Locale aus `LOCALE` verwendet.
 *
 * Die Funktionen sind reine Wrapper um `Date.prototype.toLocaleDateString`/
 * `toLocaleTimeString` und bleiben options-kompatibel zum Bestand. Die eigentliche
 * Umstellung der Aufrufer erfolgt in späteren Tasks (7.4/7.5) über `useI18n()`.
 */

import type { Language } from "./catalog";

/** Zuordnung Unterstützte_Sprache → BCP-47-Locale für die Intl-Formatierung. */
const LOCALE: Record<Language, string> = { de: "de-DE", en: "en-GB" };

/**
 * Formatiert ein ISO-Datum sprachabhängig.
 *
 * Default-Optionen entsprechen der bestehenden `ProfilePage`-Darstellung
 * (`weekday`, `day`, `month`, `year`, UTC). Aufrufer können die Optionen
 * überschreiben.
 */
export function formatDate(
  language: Language,
  iso: string,
  opts: Intl.DateTimeFormatOptions = {
    weekday: "short",
    day: "2-digit",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  },
): string {
  return new Date(iso).toLocaleDateString(LOCALE[language], opts);
}

/**
 * Formatiert eine ISO-Zeitangabe sprachabhängig.
 *
 * Ohne explizite Optionen greift das Locale-Standardformat der jeweiligen
 * Sprache (analog zum bisherigen `toLocaleTimeString(undefined, …)`-Verhalten,
 * nur sprachgebunden).
 */
export function formatTime(
  language: Language,
  iso: string,
  opts?: Intl.DateTimeFormatOptions,
): string {
  return new Date(iso).toLocaleTimeString(LOCALE[language], opts);
}
